import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import RobotFace from "@/components/RobotFace";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { claimAnonChat, postAuthPath, activatedSummary } from "@/lib/claimAnonChat";

/**
 * Handles the return leg of Emergent Google Auth.
 * Reads `session_id` from the URL fragment, exchanges it via the backend,
 * refreshes the AuthProvider, and lands the user in /app.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh, setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sid = match?.[1];

    // Strip the fragment immediately so it isn't reused
    if (window.history?.replaceState) {
      window.history.replaceState(null, "", window.location.pathname);
    }

    if (!sid) {
      toast.error("Missing Google session — please try again.");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post(
          "/auth/oauth/session",
          {},
          { headers: { "X-Session-ID": sid } }
        );
        setUser(data);
        const claimed = await claimAnonChat();
        toast.success(claimed
          ? `Welcome, ${data.name?.split(" ")[0] || "friend"} — I kept our chat`
          : `Welcome, ${data.name?.split(" ")[0] || "friend"}`);
        const switchedOn = activatedSummary(claimed);
        if (switchedOn) setTimeout(() => toast.success(switchedOn), 900);
        nav(postAuthPath(claimed), { replace: true });
      } catch (e) {
        console.error("oauth exchange failed", e);
        toast.error("Google sign-in failed. Please try again.");
        try { await refresh(); } catch {}
        nav("/login", { replace: true });
      }
    })();
  }, [nav, refresh, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <RobotFace expression="thinking" size={220}/>
        <div className="mt-6 font-display text-xl">Signing you in…</div>
        <div className="mt-1 text-sm text-[var(--rammy-text-muted)] font-mono">Handing the keys to Rammy</div>
      </div>
    </div>
  );
}
